import { Card, CardColor, GameState, PreparedCard, CardType, GamePhase } from '../types'
import { t, Language } from '../data/translations'

export const groupHandForDisplay = (hand: Card[]): { key: string; topCard: Card; cards: Card[]; count: number }[] => {
  const handDisplayItems: { key: string; topCard: Card; cards: Card[]; count: number }[] = []
  const processedCardIds = new Set<string>()
  const groupedByColor = hand
    .filter(c => !c.essenceCost)
    .reduce((acc, card) => {
      if (!acc[card.color]) acc[card.color] = []
      acc[card.color].push(card)
      return acc
    }, {} as Record<CardColor, Card[]>)
  for (const card of hand) {
    if (processedCardIds.has(card.id)) continue
    if (card.essenceCost) {
      handDisplayItems.push({ key: card.id, topCard: card, cards: [card], count: 1 })
      processedCardIds.add(card.id)
    } else {
      const stack = groupedByColor[card.color]
      if (stack) {
        handDisplayItems.push({ key: card.color, topCard: stack[0], cards: stack, count: stack.length })
        stack.forEach(c => processedCardIds.add(c.id))
        delete groupedByColor[card.color]
      }
    }
  }
  return handDisplayItems
}

export const isCardVisibleDueToDiscerningEye = (state: GameState | null, card: PreparedCard, ownerId: number): boolean => {
  // Allow visibility during both day (Preparation) and night (Combat) phases
  if (!state || !state.players || (state.phase !== GamePhase.Preparation && state.phase !== GamePhase.Combat)) return false
  const owner = state.players.find(p => p.id === ownerId)
  if (!owner) return false
  // Effect applies when appliedRound is state.round - 1
  // Night N (apply) → Day N+1 (round = N+1, appliedRound = N, visible) → Night N+1 (round = N+1, visible)
  const hasActiveEffect = owner.effectCards.some(c => c.type === CardType.DiscerningEye && c.appliedRound === state.round - 1)
  return hasActiveEffect
}

// Psy-Blocked cards should be visible to both players (they were revealed when blocked)
export const isCardVisibleDueToPsyBlock = (card: PreparedCard): boolean => {
  return !!card.lockedState
}

export const cleanupHandRotations = (
  state: GameState | null,
  localPlayerId: number,
  handCardRotations: Record<string, number>,
): Record<string, number> => {
  if (!state || !state.players) return handCardRotations
  const player = state.players.find(p => p.id === localPlayerId)
  if (!player) return handCardRotations
  const playerHandIds = new Set(player.hand.map(c => c.id))
  const currentRotationKeys = Object.keys(handCardRotations)
  let changed = false
  const newRotations = { ...handCardRotations }
  for (const cardId of currentRotationKeys) {
    if (!playerHandIds.has(cardId)) {
      delete newRotations[cardId]
      changed = true
    }
  }
  return changed ? newRotations : handCardRotations
}

export type TargetingArrowData = {
  start: { x: number; y: number }
  end: { x: number; y: number } | null
  color: CardColor
}

export const getTargetingArrowData = (
  targeting: { card: PreparedCard; startRect: DOMRect } | null,
  mousePos: { x: number; y: number } | null,
  aiTargetingArrow: TargetingArrowData | null,
): TargetingArrowData | null => {
  if (aiTargetingArrow) return aiTargetingArrow
  if (targeting && mousePos) {
    return {
      start: {
        x: targeting.startRect.left + targeting.startRect.width / 2,
        y: targeting.startRect.top + targeting.startRect.height / 2,
      },
      end: mousePos,
      color: targeting.card.card.color,
    }
  }
  return null
}

export const getPhaseMessage = (
  state: GameState,
  localPlayerId: number,
  playerIsReady: boolean,
  language: Language
): string => {
  const { phase, turnPlayerId } = state
  if (!state.players || !state.activations) return ''
  if (state.towerChangeMenu) return t('phase.transformTower', language)
  // Check if there's any active (unresolved, unaccepted) activation
  const hasActiveActivation = state.activations.some(act => !act.resolved && !act.accepted)
  if (hasActiveActivation) {
    // Use lastCardPlayerId as the source of truth for turn order
    // If opponent played the last card, it's our turn to respond
    const isMyTurnToAct = state.lastCardPlayerId !== undefined &&
      state.lastCardPlayerId !== localPlayerId
    return isMyTurnToAct ? t('phase.yourResponse', language) : t('phase.opponentResponding', language)
  }
  switch (phase) {
    case GamePhase.TowerChoice: {
      const me = state.players.find(p => p.id === localPlayerId)!
      const opp = state.players.find(p => p.id !== localPlayerId)!
      const myTowerNeeded = me.towers.length === 0 && state.turnPlayerId === localPlayerId
      const myHeroNeeded = me.towers.length > 0 && !me.hasChosenHeroAndClass && state.heroClassTurnId === localPlayerId
      if (myTowerNeeded) return t('phase.claimTower', language, { round: String(state.round) })
      if (myHeroNeeded) return t('phase.chooseHeroClass', language, { round: String(state.round) })
      const oppPending = (opp.towers.length === 0) || !opp.hasChosenHeroAndClass
      return oppPending ? t('phase.waitingOtherPlayer', language) : t('phase.setupComplete', language, { round: String(state.round) })
    }
    case GamePhase.TowerPurchase:
      return t('phase.acquireTower', language)
    case GamePhase.AWAITING_PURIFICATION_TARGET:
      return t('phase.choosePurification', language)
    case GamePhase.StashChoice: {
      const options = state.stashChoiceOptions[localPlayerId] || [];
      return options.length > 0
        ? t('phase.choosePower', language, { round: String(state.round) })
        : t('phase.opponentChoosingStash', language);
    }
    case GamePhase.LockedCardOverflow:
      return t('phase.resolveOverflow', language)
    case GamePhase.Preparation: {
      if (playerIsReady) return t('phase.waiting', language)
      const firstNightPlayerName = state.players.find(p => p.id === state.firstPlayerIdForNight)?.name || ''
      return t('phase.prepare', language, { round: String(state.round), name: firstNightPlayerName })
    }
    case GamePhase.Combat:
      return turnPlayerId === localPlayerId ? t('phase.yourTurn', language) : t('phase.opponentTurn', language)
    case GamePhase.RoundEnd:
      return t('phase.nightEnds', language, { round: String(state.round) })
    case GamePhase.GameOver:
      return t('phase.gameOver', language)
    default:
      return ''
  }
}

export const calcOpponentHandSpacing = (opponentHandCount: number): number => {
  return Math.max(15, 50 - opponentHandCount * 3)
}

export const calcIsHandScrollable = (
  handDisplayCount: number,
  containerWidth: number,
): boolean => {
  return handDisplayCount * 80 > containerWidth
}
